import React, { ReactNode, useContext } from 'react';
import * as types from '../types';
import AppPaths, { SubscriptionCheckoutPaths, SubscriptionManageCheckoutPaths } from '../AppPaths';

interface BaseCartMode {
  allowPromoCodes: boolean;
  cartSelector: (state: types.AppState) => types.Cart;
  checkoutType: types.CheckoutType;
  checkoutContext?: types.PaymentContext;
  rootPath: string;
  showPayPal: boolean;
  /**
   * Whether the post-purchase supplements upsell may be shown for this flow. Defaults to true when
   * omitted. The Verizon flow opts out so the supplements upsell never appears in its checkout
   * (those products aren't part of the Verizon experience).
   */
  offersSupplements?: boolean;
  /**
   * When true, the cart shipping/returns badge reads "30 day return policy" instead of the default
   * "30 days risk free". The Verizon flow uses the return-policy framing.
   */
  returnPolicyCopy?: boolean;
}

interface DefaultCartMode extends BaseCartMode {
  checkoutType: types.CheckoutType.Store;
}

interface UpgradeCartMode extends BaseCartMode {
  checkoutType: types.CheckoutType.Upgrade;
}

interface VerizonCartMode extends BaseCartMode {
  checkoutType: types.CheckoutType.Verizon;
}

interface SubscriptionCartMode extends BaseCartMode {
  checkoutType:
    | types.CheckoutType.Subscription
    | types.CheckoutType.MembershipUpgrade
    | types.CheckoutType.MonthlyUpgrade;
  checkoutPaths: SubscriptionCheckoutPaths | SubscriptionManageCheckoutPaths;
}

type CartMode = DefaultCartMode | UpgradeCartMode | SubscriptionCartMode | VerizonCartMode;

export class CartModes {
  static readonly default: DefaultCartMode = {
    allowPromoCodes: true,
    cartSelector: (state: types.AppState) => state.storeShop.cart,
    checkoutType: types.CheckoutType.Store,
    rootPath: AppPaths.Home,
    showPayPal: true,
  };

  static readonly verizon: VerizonCartMode = {
    allowPromoCodes: false,
    cartSelector: (state: types.AppState) => state.verizonShop.cart,
    checkoutType: types.CheckoutType.Verizon,
    offersSupplements: false,
    returnPolicyCopy: true,
    rootPath: AppPaths.Verizon.Root,
    showPayPal: true,
  };

  static readonly series3: UpgradeCartMode = {
    allowPromoCodes: true,
    cartSelector: (state: types.AppState) => state.series3UpgradeShop.cart,
    checkoutType: types.CheckoutType.Upgrade,
    rootPath: AppPaths.Series3Upgrade.Root,
    showPayPal: false,
  };

  static readonly series3UpgradeV2: UpgradeCartMode = {
    allowPromoCodes: true,
    cartSelector: (state: types.AppState) => state.series3UpgradeShop.cart,
    checkoutType: types.CheckoutType.Upgrade,
    rootPath: AppPaths.Series3UpgradeV2.Root,
    showPayPal: false,
  };

  // Membership upgrade, purchase subscription, and manage subscription flows are
  // similar in that they're all meant to purchase a subscription for a specific device
  // so they can all share a cart model.
  static readonly purchaseSubscription: SubscriptionCartMode = {
    allowPromoCodes: true,
    cartSelector: (state: types.AppState) => state.subscriptionShop.cart,
    checkoutPaths: AppPaths.PurchaseSubscription,
    checkoutType: types.CheckoutType.Subscription,
    checkoutContext: types.PaymentContext.PURCHASE_SUBSCRIPTION,
    rootPath: AppPaths.PurchaseSubscription.Root,
    showPayPal: false,
  };

  static readonly membershipUpgrade: SubscriptionCartMode = {
    allowPromoCodes: false,
    cartSelector: (state: types.AppState) => state.subscriptionShop.cart,
    checkoutPaths: AppPaths.MembershipUpgrade,
    checkoutType: types.CheckoutType.MembershipUpgrade,
    checkoutContext: types.PaymentContext.MEMBERSHIP_UPGRADE,
    rootPath: AppPaths.MembershipUpgrade.Root,
    showPayPal: false,
  };

  static readonly monthlyUpgrade: SubscriptionCartMode = {
    allowPromoCodes: false,
    cartSelector: (state: types.AppState) => state.subscriptionShop.cart,
    checkoutPaths: AppPaths.MonthlyUpgrade,
    checkoutType: types.CheckoutType.MonthlyUpgrade,
    checkoutContext: types.PaymentContext.MONTHLY_UPGRADE,
    rootPath: AppPaths.MonthlyUpgrade.Root,
    showPayPal: false,
  };

  static readonly manageSubscription: SubscriptionCartMode = {
    allowPromoCodes: false,
    cartSelector: (state: types.AppState) => state.subscriptionShop.cart,
    checkoutPaths: AppPaths.Subscription,
    checkoutType: types.CheckoutType.MembershipUpgrade,
    checkoutContext: types.PaymentContext.MANAGE_SUBSCRIPTION,
    rootPath: AppPaths.Subscription.SplashPageForDevice(),
    showPayPal: true,
  };
}

const CartModeContext = React.createContext<CartMode>(CartModes.default);

export function CartModeProvider({ mode, children }: { mode: CartMode; children: ReactNode }) {
  return <CartModeContext.Provider value={mode}>{children}</CartModeContext.Provider>;
}

export function useCartMode(): CartMode {
  return useContext(CartModeContext);
}

export function useSubscriptionCartMode(): SubscriptionCartMode {
  const cartMode = useCartMode();
  if (
    cartMode.checkoutType !== types.CheckoutType.Subscription &&
    cartMode.checkoutType !== types.CheckoutType.MembershipUpgrade
  ) {
    throw new Error('useSubscriptionCartMode() can only be used with subscription cart modes');
  }

  return cartMode;
}
